"use client";

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/auth/use-auth";
import type { NotificationChannelType } from "@/lib/notifications/types";
import { useTranslations } from "next-intl"


/** One captured Telegram chat (a delivery target). */
export interface CapturedChat {
  id: string;
  label?: string;
}

/** A channel as returned by the API (secrets stripped); `config` is type-specific. */
export interface NotificationChannelView {
  id: string;
  type: NotificationChannelType;
  name: string;
  enabled: boolean;
  /** Platform transport; the row is the tenant's but sends from the platform mailbox — not deletable, sender not theirs to pick. */
  isSystem: boolean;
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface EmailChannelConfigInput {
  recipients: string[];
  from_email?: string | null;
  subject_prefix?: string | null;
}

/** Sanitized delivery outcome from the test-send endpoint. */
export interface TestDelivery {
  channelType: NotificationChannelType;
  status: "delivered" | "skipped" | "failed";
  publicMessage: string;
}

const BASE = "/api/notifications/channels";

async function readError(response: Response, fallback: string): Promise<string> {
  try {
    const data = await response.json();
    return data.error || fallback;
  } catch {
    return fallback;
  }
}

export function useNotificationChannels() {
  const tUi = useTranslations("Feedback.UseNotificationChannels")

  const [channels, setChannels] = useState<NotificationChannelView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { fetchWithAuth, isAuthenticated, isLoading: isAuthLoading } = useAuth();

  const loadChannels = useCallback(async () => {
    if (isAuthLoading) return;
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }
    setError(null);
    try {
      const response = await fetchWithAuth(BASE);
      if (response.ok) {
        const data = await response.json();
        setChannels(data.channels || []);
      } else {
        setError(await readError(response, "Failed to load channels"));
      }
    } catch (err) {
      console.error("Error loading notification channels:", err);
      setError(tUi("failedToLoadChannels"));
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth, isAuthenticated, isAuthLoading]);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  const upsertLocal = useCallback((channel: NotificationChannelView) => {
    setChannels((prev) => {
      const exists = prev.some((c) => c.id === channel.id);
      return exists ? prev.map((c) => (c.id === channel.id ? channel : c)) : [channel, ...prev];
    });
  }, []);

  const createEmailChannel = useCallback(
    async (name: string, config: EmailChannelConfigInput): Promise<NotificationChannelView> => {
      const response = await fetchWithAuth(BASE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "email", name, config }),
      });
      if (!response.ok) throw new Error(await readError(response, "Failed to create channel"));
      const { channel } = await response.json();
      upsertLocal(channel);
      return channel;
    },
    [fetchWithAuth, upsertLocal],
  );

  const connectTelegram = useCallback(
    async (name: string, botToken: string): Promise<NotificationChannelView> => {
      const response = await fetchWithAuth(`${BASE}/telegram/connect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, botToken }),
      });
      if (!response.ok) throw new Error(await readError(response, "Failed to connect Telegram bot"));
      const { channel } = await response.json();
      upsertLocal(channel);
      return channel;
    },
    [fetchWithAuth, upsertLocal],
  );

  const updateChannel = useCallback(
    async (
      id: string,
      patch: { name?: string; enabled?: boolean; config?: Record<string, unknown> },
    ): Promise<NotificationChannelView> => {
      const response = await fetchWithAuth(`${BASE}/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!response.ok) throw new Error(await readError(response, "Failed to update channel"));
      const { channel } = await response.json();
      upsertLocal(channel);
      return channel;
    },
    [fetchWithAuth, upsertLocal],
  );

  const deleteChannel = useCallback(
    async (id: string): Promise<void> => {
      const response = await fetchWithAuth(`${BASE}/${id}`, { method: "DELETE" });
      if (!response.ok) throw new Error(await readError(response, "Failed to delete channel"));
      setChannels((prev) => prev.filter((c) => c.id !== id));
    },
    [fetchWithAuth],
  );

  const removeChat = useCallback(
    async (id: string, chatId: string): Promise<NotificationChannelView> => {
      const response = await fetchWithAuth(`${BASE}/${id}/chats/${encodeURIComponent(chatId)}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error(await readError(response, "Failed to remove chat"));
      const { channel } = await response.json();
      upsertLocal(channel);
      return channel;
    },
    [fetchWithAuth, upsertLocal],
  );

  const testChannel = useCallback(
    async (id: string): Promise<TestDelivery> => {
      const response = await fetchWithAuth(`${BASE}/${id}/test`, { method: "POST" });
      if (!response.ok) throw new Error(await readError(response, "Failed to send test notification"));
      const { delivery } = await response.json();
      return delivery;
    },
    [fetchWithAuth],
  );

  return {
    channels,
    isLoading,
    error,
    refresh: loadChannels,
    createEmailChannel,
    connectTelegram,
    updateChannel,
    deleteChannel,
    removeChat,
    testChannel,
  };
}
